// import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'

import { fbEnrichUsersFromIds } from '../../../data/FirebaseEnrich'
import { COLLECTORS_QUERY } from '../../../graphql/graphql-queries'
import { Query_Root, TokenAccount } from '../../../graphql/indexer-graphql-types'
import { useInfiniteLoadIndexer } from '../../../hooks/infiniteLoadIndexer'
import { VideoData } from '../../../interfaces'
import { FbEnrichedUser } from '../../../interfaces/User'
import LeaderboardItem from '../leaderboard/LeaderboardItem'
import { SmallSpinner, SmallSpinnerContainer } from '../../spinner'
import { NoVideosYet } from '../common'
import { ownerComparer } from '../../../utils/comparer'

const itemsContainerStyle = `
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled(InfiniteScroll)`
  ${itemsContainerStyle}
`

interface Props {
  video: VideoData
  currentIndex: number
  index: number
  updateHeight: () => void
}

export type EnrichedTokenAccount = Omit<TokenAccount, 'owner'> & {
  owner: FbEnrichedUser
}

const parseItems = (data: Query_Root | undefined): TokenAccount[] => {
  if (!data) {
    return []
  }

  const { mint: mints } = data
  const mint = mints[0]

  if (!mint?.tokenAccounts?.length) {
    return []
  }

  const { tokenAccounts } = mint

  return tokenAccounts.filter((tokenAccount) => !!tokenAccount.owner)
}

const enrichItems = async (tokenAccounts: TokenAccount[]): Promise<EnrichedTokenAccount[]> => {
  if (!tokenAccounts.length) {
    return []
  }

  const userIds: string[] = tokenAccounts.map((tokenAccount) => tokenAccount.owner) as string[]
  const users = await fbEnrichUsersFromIds(userIds)
  return tokenAccounts.map(
    (tokenAccount) =>
      ({
        ...tokenAccount,
        owner: users.find((user) => user.address === tokenAccount.owner),
      } as EnrichedTokenAccount)
  )
}

const LeaderboardDetails = ({ video, currentIndex, index, updateHeight }: Props): JSX.Element => {
  const {
    data: items,
    fetchNext,
    hasNext,
    refetch,
  } = useInfiniteLoadIndexer<EnrichedTokenAccount, TokenAccount>({
    id: video.address,
    query: COLLECTORS_QUERY,
    enrichItems,
    parseItems,
    comparer: ownerComparer,
    page_size: 10,
    variables: { mint: video.address },
    refetchMarker: 'activity',
  })

  React.useEffect(() => {
    if (index === currentIndex) {
      refetch()
    }
  }, [currentIndex, index])

  React.useEffect(() => {
    if (index === currentIndex) {
      updateHeight()
    }
  }, [items])

  if (items.length === 0) {
    return hasNext ? (
      <StyledSkeletonContainer>
        {[...Array(1).keys()].map((index) => (
          <LeaderboardItem key={`skeleton-item-${index}`} />
        ))}
      </StyledSkeletonContainer>
    ) : (
      <NoVideosYet>{`No collectors yet.`}</NoVideosYet>
    )
  }

  return (
    <StyledInfiniteScroll
      loadMore={fetchNext}
      hasMore={hasNext}
      loader={
        <SmallSpinnerContainer>
          <SmallSpinner />
        </SmallSpinnerContainer>
      }
      useWindow
    >
      {' '}
      {items.map(
        (item, i) =>
          item && (
            <LeaderboardItem tokenAccount={item} index={i + 1} key={`leaderboard-item-${i}`} />
          )
      )}
    </StyledInfiniteScroll>
  )
}

export default LeaderboardDetails
